import clsx from 'clsx';
import Button from 'components/Button';
import Overlay from 'components/Layout/Overlay';
import Table from 'components/Table/TableFunctional';
import TooltipWrapper from 'components/Tooltip';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import Truncator from 'components/Truncator';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {FC, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Selection} from 'react-stately';
import {useBackend} from 'state/backend';
import {c, t} from 'ttag';
import {Backend} from 'types/api';
import {jstDateString} from 'utils/date';
import {onApiKeyError} from 'utils/request/result';

import DocumentInformation from '../components/Inbox/DocumentInformation';
import DownloadNotificationWarning from '../components/Inbox/DownloadNotificationWarning';
import InboxSectionHeader from '../components/Inbox/InboxHeader';
import InboxLayout from '../components/Inbox/InboxLayout';
import InboxPagination from '../components/Inbox/InboxPagination';
import InboxTableLayout from '../components/Inbox/TableLayout';
import {
    DocumentCheckboxFormFields,
    PaginationData,
    PortalPermissions,
} from '../components/Inbox/types';
import {isDocumentType} from '../components/Inbox/utils';
import DownloadDocumentModal from '../DownloadDocumentModal';
import usePortalLongTask from '../hooks/usePortalLongTask';
import {
    ClientInboxDocument,
    PersonalInboxFormFields,
    usePostDownloadPickupItems,
} from '../services';

export type PersonalInboxBackend = Backend<PersonalInboxBackendData>;

export type PersonalInboxBackendData = {
    clientName: string;
    documents: ClientInboxDocument[];
    email: string;
    pagination: PaginationData;
    partnerName: string;
    permissions: PortalPermissions;
    portalMid: string;
    sessionExpiration: number;
};

const PersonalInbox: FC = () => {
    const {
        data: {
            clientName,
            documents,
            email,
            pagination,
            partnerName,
            permissions,
            portalMid,
            sessionExpiration,
        },
        flags,
    } = useBackend<PersonalInboxBackend>();
    const methods = useForm<DocumentCheckboxFormFields>();
    const {mutateAsync} = usePostDownloadPickupItems();
    const [rows, setRows] = useState<ClientInboxDocument[]>(
        documents.map((d) => ({...d, selected: false})),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [bulkDownloadDisabled, setBulkDownloadDisabled] = useState<boolean>(
        rows.filter((d) => d.selected).map((d) => d.mid).length <= 1,
    );
    const [infoOverlay, setInfoOverlay] = useState<{
        document: ClientInboxDocument;
        element: HTMLElement;
    } | null>(null);

    const refOverlayContent = useRef<HTMLDivElement>(null);
    useOutsideDetector([refOverlayContent], () => setInfoOverlay(null));

    const {
        downloadRef,
        longTask,
        longTaskApiError,
        onFinish,
        progressRef,
        setLongTask,
    } = usePortalLongTask(portalMid, () => {
        setIsOpen(false);
        onFinish();
    });

    const onClose = () => {
        setIsOpen(false);
        onFinish();
    };

    const showZipWarning = rows.some(
        (document) => document.filename.slice(-3) === 'zip',
    );

    const onClickDownload = () => {
        setIsOpen(true);
        methods.clearErrors('root.downloadResult');

        const formData: PersonalInboxFormFields = {
            pickupAuthorizations: rows
                .filter((document) => document.selected)
                .map((document) => document.mid),
            selectAll: false, // Only ddl the current page of selected documents, not all pages.
        };
        mutateAsync({formData, portalMid})
            .then((res) => {
                if (res) setLongTask(res.data);
            })
            .catch((error) => {
                onApiKeyError(methods, 'downloadResult')(error);
            });
    };

    const onSelectionChange = (selection: Selection) => {
        setInfoOverlay(null); // Stop showing current overlay when selecting something else, by cliking anywhere in the Table

        const selected = [...rows];

        if (selection.toString() === 'all') {
            setBulkDownloadDisabled(false);
            setRows(
                selected.map((document) => ({...document, selected: true})),
            );
        } else {
            const mids = [...selection];
            setBulkDownloadDisabled(mids.length < 2);
            setRows(
                selected.map((document) =>
                    [...mids].includes(document.mid)
                        ? {...document, selected: true}
                        : {...document, selected: false},
                ),
            );
        }
    };

    return (
        <InboxLayout
            clientName={clientName}
            email={email}
            flags={flags}
            navigation="personal-inbox"
            partnerName={partnerName}
            permissions={permissions}
            portalMid={portalMid}
            sessionExpiration={sessionExpiration}
        >
            <FormProvider {...methods}>
                <InboxSectionHeader
                    buttonOnclick={onClickDownload}
                    isButtonDisabled={bulkDownloadDisabled}
                    subtitle={t`All items sent to the address ${email}`}
                    title={t`Personal Inbox`}
                />
                {showZipWarning && <DownloadNotificationWarning />}
                <InboxTableLayout>
                    <Table
                        aria-label="personal-inbox"
                        classNameTable="table-fixed"
                        onSelectionChange={onSelectionChange}
                        selectionMode={
                            documents.length > 0 ? 'multiple' : 'none'
                        }
                    >
                        <Table.Header>
                            <Table.Column className="w-28">
                                {c('portal').t`Received`}
                            </Table.Column>
                            <Table.Column className="w-[7.375rem]">
                                {c('portal').t`Type`}
                            </Table.Column>
                            <Table.Column className="w-[18.5rem]">
                                {c('portal').t`Document`}
                            </Table.Column>
                            <Table.Column>
                                {c('portal').t`Project`}
                            </Table.Column>
                            <Table.Column className="w-[7.438rem] text-end">
                                {t`Total`}
                            </Table.Column>
                            <Table.Column className="w-24"> </Table.Column>
                        </Table.Header>
                        {rows.length > 0 ? (
                            <Table.Body items={rows}>
                                {(document) => (
                                    <Table.Row
                                        aria-label={`document-row-${document.mid}`}
                                        key={document.mid}
                                    >
                                        <Table.Cell preventPointerEvents={true}>
                                            {jstDateString(
                                                new Date(document.dateSent),
                                            )}
                                        </Table.Cell>
                                        <Table.Cell preventPointerEvents={true}>
                                            <Truncator
                                                className={clsx(
                                                    isDocumentType(document)
                                                        ? 'bg-blue-50 text-blue-800'
                                                        : 'bg-grey-200 text-grey-800',
                                                    'w-fit max-w-full rounded-full px-2 py-0.5 text-sm font-semibold',
                                                )}
                                                text={document.documentTypeName}
                                            />
                                        </Table.Cell>
                                        <Table.Cell
                                            colSpan={
                                                isDocumentType(document) ? 1 : 3
                                            }
                                            preventPointerEvents={true}
                                        >
                                            {isDocumentType(document) ? (
                                                <div className="flex">
                                                    {document.dateCancelled && (
                                                        <TooltipIcon
                                                            className="my-0.5 mr-2 text-gold-700"
                                                            icon="warning"
                                                            message={t`Cancelled by Sender`}
                                                            size="base"
                                                        />
                                                    )}
                                                    <Truncator
                                                        className="max-w-full"
                                                        text={
                                                            document.documentNumber ||
                                                            ''
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <Truncator
                                                    className="max-w-full"
                                                    showFilenameExtension={true}
                                                    text={
                                                        document.filename || ''
                                                    }
                                                    truncateAt="middle"
                                                />
                                            )}
                                        </Table.Cell>
                                        <Table.Cell
                                            className={clsx(
                                                !isDocumentType(document) &&
                                                    'hidden',
                                            )}
                                            colSpan={
                                                isDocumentType(document) ? 1 : 0
                                            }
                                            preventPointerEvents={true}
                                        >
                                            {isDocumentType(document) && (
                                                <Truncator
                                                    className="max-w-full"
                                                    text={
                                                        document.projectName ||
                                                        ''
                                                    }
                                                />
                                            )}
                                        </Table.Cell>
                                        <Table.Cell
                                            className={clsx(
                                                !isDocumentType(document) &&
                                                    'hidden',
                                            )}
                                            colSpan={
                                                isDocumentType(document) ? 1 : 0
                                            }
                                            preventPointerEvents={true}
                                        >
                                            {isDocumentType(document) &&
                                                document.hasTotals && (
                                                    <Truncator
                                                        className="float-right max-w-full"
                                                        text={document.total}
                                                    />
                                                )}
                                        </Table.Cell>
                                        <Table.Cell
                                            className="float-right"
                                            preventPointerEvents={true}
                                        >
                                            <TooltipWrapper
                                                message={t`Download`}
                                            >
                                                <Button
                                                    icon="download"
                                                    kind="tertiary"
                                                    onClick={() => {
                                                        const newWindow: null | Window =
                                                            window.open(
                                                                `/portal/${portalMid}/file-download/${document.mid}/`,
                                                            );

                                                        if (newWindow) {
                                                            newWindow.opener =
                                                                null;
                                                        }
                                                    }}
                                                    rel="noopener noreferrer"
                                                    size="xs"
                                                />
                                            </TooltipWrapper>
                                            <Button
                                                aria-label="info"
                                                icon="info"
                                                kind="tertiary"
                                                onClick={(event) => {
                                                    setInfoOverlay({
                                                        document,
                                                        element:
                                                            event.currentTarget as HTMLElement,
                                                    });
                                                }}
                                                size="xs"
                                            />
                                            <Button
                                                aria-label="open-blank"
                                                icon="open_blank"
                                                kind="tertiary"
                                                onClick={() => {
                                                    const newWindow: null | Window =
                                                        window.open(
                                                            document.url,
                                                            '_blank',
                                                        );

                                                    if (newWindow) {
                                                        newWindow.opener = null;
                                                    }
                                                }}
                                                rel="noopener noreferrer"
                                                size="xs"
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table.Body>
                        ) : (
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell>
                                        <div className="w-16 whitespace-nowrap text-grey-600">
                                            {t`No documents to display. You either haven't received any Documents recently, or your search terms are too strict.`}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                    <Table.Cell>{null}</Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        )}
                    </Table>
                </InboxTableLayout>
                <InboxPagination
                    currentPage={pagination.currentPage}
                    numPages={pagination.numPages}
                    url={`/portal/${portalMid}/personal-inbox/`}
                />
                <DownloadDocumentModal
                    downloadRef={downloadRef}
                    isOpen={isOpen}
                    longTask={longTask}
                    longTaskApiError={longTaskApiError}
                    onClose={onClose}
                    progressRef={progressRef}
                    selectedDocuments={rows}
                />
            </FormProvider>
            {infoOverlay?.document && (
                <Overlay
                    element={infoOverlay.element}
                    layoutLayer="z-tooltip"
                    layoutMethod="left"
                >
                    <div
                        aria-label="info-overlay"
                        className="elevation-400 pointer-events-auto relative min-w-[30rem] rounded border-grey-200 bg-white p-4 text-base"
                        ref={refOverlayContent}
                        role="dialog"
                    >
                        <Button
                            aria-label="close"
                            className="absolute right-0 top-0 m-2"
                            icon="close_small"
                            kind="tertiary"
                            onClick={() => setInfoOverlay(null)}
                            size="xs"
                        />
                        <DocumentInformation
                            document={infoOverlay.document}
                            isZipFile={
                                infoOverlay.document.filename.slice(-3) ===
                                'zip'
                            }
                        />
                    </div>
                </Overlay>
            )}
        </InboxLayout>
    );
};

export default PersonalInbox;
